import { useCallback, useEffect, useLayoutEffect, useState } from 'react'

import type { WalletWriteProgressPhase } from '../../domain/wallet/walletSigningFlow'
import type { Web3Library } from '../../domain/wallet/walletContext'
import {
  abortMobileLiquidityWalletSession,
  buildLiquiditySessionSummary,
  clearMobileLiquiditySession,
  completeMobileLiquiditySession,
  isStaleMobileLiquidityPendingSession,
  MOBILE_LIQUIDITY_SESSION_EVENT,
  patchMobileLiquiditySession,
  readMobileLiquiditySession,
  writeMobileLiquiditySession,
  type MobileLiquidityOperationKind,
  type MobileLiquiditySession,
} from '../../domain/liquidity/mobileLiquiditySession'
import { useLiquidityWalletReturnSync } from '../../domain/liquidity/useLiquidityWalletReturnSync'

type UseLiquidityMobileSessionOptions = {
  kind: MobileLiquidityOperationKind
  account?: string | null
  chainId?: number | null
  token0?: string
  token1?: string
  library?: Web3Library | null
  attemptingTxn: boolean
  walletProgressPhase: WalletWriteProgressPhase | null
  setAttemptingTxn: (value: boolean) => void
  setWalletProgressPhase: (value: WalletWriteProgressPhase | null) => void
  setWalletErrorMessage: (value: string | null) => void
  setShowConfirm: (value: boolean) => void
  setTxHash: (value: string) => void
  onWalletRejected: (message: string) => void
}

export function useLiquidityMobileSession({
  kind,
  account,
  chainId,
  token0,
  token1,
  library,
  attemptingTxn,
  walletProgressPhase,
  setAttemptingTxn,
  setWalletProgressPhase,
  setWalletErrorMessage,
  setShowConfirm,
  setTxHash,
  onWalletRejected,
}: UseLiquidityMobileSessionOptions) {
  const [mobileLiquiditySession, setMobileLiquiditySession] = useState<MobileLiquiditySession | null>(
    () => readMobileLiquiditySession(kind),
  )

  const applySessionToState = useCallback(
    (session: MobileLiquiditySession) => {
      if (session.kind !== kind) return

      const hash = session.hash
      if (!hash) {
        if (session.phase === 'wallet' || session.phase === 'submitted') {
          setWalletProgressPhase(session.phase === 'wallet' ? 'wallet' : 'submitted')
          setShowConfirm(true)
          setAttemptingTxn(true)
          setWalletErrorMessage(null)
        }
        return
      }

      // Hash present: pantalla de éxito (no reabrir espera de firma).
      setWalletProgressPhase(null)
      setShowConfirm(false)
      setAttemptingTxn(false)
      setTxHash(hash)
      setWalletErrorMessage(null)
    },
    [
      kind,
      setAttemptingTxn,
      setShowConfirm,
      setTxHash,
      setWalletErrorMessage,
      setWalletProgressPhase,
    ],
  )

  const writeSessionForReview = useCallback(() => {
    if (!account || !chainId || !token0 || !token1) return
    writeMobileLiquiditySession({
      kind,
      chainId,
      account,
      token0,
      token1,
      summary: buildLiquiditySessionSummary(kind, token0, token1),
      phase: 'review',
      updatedAt: Date.now(),
    })
    setMobileLiquiditySession(readMobileLiquiditySession(kind))
  }, [account, chainId, kind, token0, token1])

  const patchSessionWallet = useCallback(() => {
    patchMobileLiquiditySession({ phase: 'wallet' })
    setMobileLiquiditySession(readMobileLiquiditySession(kind))
  }, [kind])

  const patchSessionSubmitted = useCallback((hash: string) => {
    patchMobileLiquiditySession({ hash, phase: 'submitted' })
    setMobileLiquiditySession(readMobileLiquiditySession(kind))
  }, [kind])

  const clearSession = useCallback(() => {
    abortMobileLiquidityWalletSession()
    setMobileLiquiditySession(readMobileLiquiditySession(kind))
  }, [kind])

  const completeSession = useCallback(() => {
    completeMobileLiquiditySession()
    setMobileLiquiditySession(null)
  }, [])

  const { trackInFlight } = useLiquidityWalletReturnSync({
    account,
    chainId,
    library,
    attemptingTxn,
    walletProgressPhase,
    onOrphanedWalletReject: onWalletRejected,
  })

  useLayoutEffect(() => {
    const session = readMobileLiquiditySession(kind)
    if (!session) return
    if (isStaleMobileLiquidityPendingSession(session)) {
      clearMobileLiquiditySession()
      setMobileLiquiditySession(null)
      setAttemptingTxn(false)
      setWalletProgressPhase(null)
      setShowConfirm(false)
      setWalletErrorMessage(null)
      setTxHash('')
    }
  }, [kind, setAttemptingTxn, setShowConfirm, setTxHash, setWalletErrorMessage, setWalletProgressPhase])

  useEffect(() => {
    if (!account || !chainId) return
    const session = readMobileLiquiditySession(kind)
    if (!session) return
    if (session.account.toLowerCase() !== account.toLowerCase()) return
    if (session.chainId !== chainId) return
    setMobileLiquiditySession(session)
    applySessionToState(session)
  }, [account, applySessionToState, chainId, kind])

  useEffect(() => {
    const onSessionUpdated = () => {
      const session = readMobileLiquiditySession(kind)
      if (!session) return
      setMobileLiquiditySession(session)
      applySessionToState(session)
    }

    window.addEventListener(MOBILE_LIQUIDITY_SESSION_EVENT, onSessionUpdated)
    return () => window.removeEventListener(MOBILE_LIQUIDITY_SESSION_EVENT, onSessionUpdated)
  }, [applySessionToState, kind])

  return {
    mobileLiquiditySession,
    writeSessionForReview,
    patchSessionWallet,
    patchSessionSubmitted,
    clearSession,
    completeSession,
    trackInFlight,
  }
}
